// src/pages/Connector/OneDrive/ListOneDriveFiles.js
import React, { useState, useEffect, useCallback } from "react";
import axiosInstance from "../../../../utils/axiosInstance";
import DataTable from "react-data-table-component";

import { Tooltip, Modal as MuiModal, Box, Button as MuiButton, TextField, Checkbox, FormControlLabel } from '@mui/material';

import EditOneDriveFileContent from "./EditOneDriveFileContent";
import SyncSettingsModalForDrive from "./SyncSettingsModalForDrive";
import PendingUpdateModalForDrive from "./PendingUpdateModalForDrive";

export default function ListOneDriveFiles({ refreshTrigger, chatbotId, permission }) {
    const [files, setFiles] = useState([]);
    const [filteredFiles, setFilteredFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);
    const [toggleCleared, setToggleCleared] = useState(false);

    const [isProcessingScrapeTrain, setIsProcessingScrapeTrain] = useState(false);
    const [showScrapeTrainSuccessText, setShowScrapeTrainSuccessText] = useState(false);
    
    // States for new modals
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSyncSettingsModalOpen, setIsSyncSettingsModalOpen] = useState(false);
    const [isPendingUpdateModalOpen, setIsPendingUpdateModalOpen] = useState(false);
    const [currentDocumentForModal, setCurrentDocumentForModal] = useState(null);

    const [refreshingRows, setRefreshingRows] = useState({}); // For individual row refresh spinner
    const [actionLoading, setActionLoading] = useState(null); // For modal save/action buttons e.g. 'syncSave', 'pendingAccept'

    const params = chatbotId ? { chatbot_id: chatbotId } : {};


    const muiModalStyle = { 
        position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
        width: { xs: '95%', sm: '90%', md: '70vw' }, maxWidth: '1000px', 
        maxHeight: '90vh', bgcolor: 'background.paper',
        border: 'none', borderRadius: '8px', boxShadow: 24, p: {xs: 2, sm: 3}, 
        display: 'flex', flexDirection: 'column', outline: 'none',
    };

    const fetchFiles = useCallback(async () => {
        setLoading(true); 
        setError(null);
        try {
            const response = await axiosInstance.get(`/api/onedrive/synced-files`, { params });
            const filesData = (response.data.data || []).map(f => ({
                ...f,
                // Ensure defaults for new fields if backend doesn't send them (though it should)
                is_auto_sync: f.is_auto_sync ?? false,
                sync_interval_days: f.sync_interval_days, // Can be null
                is_manually_edited: f.is_manually_edited ?? false,
                has_pending_update: f.has_pending_update ?? false,
                last_sync_check_date: f.last_sync_check_date,
                default_interval_days: f.default_interval_days || 7, // Fallback default
            }));
            setFiles(filesData);
            // Filtered files will be updated by useEffect watching files and searchText
        } catch (err) {
            console.error("Error fetching synced OneDrive files:", err);
            setError(err.response?.data?.message || "Error fetching synced files.");
            setFiles([]);
        } finally {
            setLoading(false);
        }
    }, []); // Empty dependency array, fetchFiles is memoized

    useEffect(() => {
        fetchFiles();
    }, [refreshTrigger, fetchFiles]);

    useEffect(() => {
        const lowerSearchText = searchText.toLowerCase();
        const filtered = files.filter((file) =>
            file.filename.toLowerCase().includes(lowerSearchText) ||
            file.mime_type.toLowerCase().includes(lowerSearchText) ||
            (file.summary && file.summary.toLowerCase().includes(lowerSearchText))
        );
        setFilteredFiles(filtered);
    }, [searchText, files]);

    const handleRefreshFile = async (dbFileId) => {
        setRefreshingRows(prev => ({ ...prev, [dbFileId]: true }));
        setError(null);
        try {
            const response = await axiosInstance.post(`/api/onedrive/refresh-file/${dbFileId}`, {}, { params });
            const updatedFile = response.data.data;
            
            // Update the file in the main 'files' state
            setFiles(prevFiles => prevFiles.map(f => (f.id === dbFileId ? { ...f, ...updatedFile } : f)));
            setError(`File ${updatedFile.filename} refreshed successfully. ${response.data.message || ''}`);
        } catch (err) {
            console.error(`Error refreshing file ${dbFileId}:`, err);
            setError(err.response?.data?.message || `Error refreshing file ${dbFileId}.`);
        } finally {
            setRefreshingRows(prev => ({ ...prev, [dbFileId]: false }));
        }
    };

    const handleDeleteFile = async (dbFileId, filename) => {
        if (!window.confirm(`Are you sure you want to remove "${filename}" from the synced list and its training data? This action does not delete the file from OneDrive.`)) {
            return;
        }
        setLoading(true); 
        setError(null);
        try {
            await axiosInstance.delete(`/api/onedrive/delete-file/${dbFileId}`, { params });
            
            setFiles(prevFiles => prevFiles.filter((f) => f.id !== dbFileId));
            setError(`"${filename}" removed successfully from synced list and training data.`);
        } catch (err) {
            console.error(`Error deleting file ${dbFileId}:`, err);
            setError(err.response?.data?.message || `Error removing "${filename}".`);
        } finally {
            setLoading(false);
        }
    };

    const handleScrapeAndTrain = async () => {
        const processableFiles = selectedRows.filter(row => !(row.is_scrape && row.is_trained) || row.is_manually_edited); // Allow re-train if manually edited or not yet trained

        if (processableFiles.length === 0) {
            setError("No files selected, or all selected files are already scraped and trained without manual edits.");
            return;
        }

        setIsProcessingScrapeTrain(true);
        setShowScrapeTrainSuccessText(false); 
        setError(''); 

        
        try {
            const fileIdsToProcess = processableFiles.map(row => row.onedrive_file_id);
            const response = await axiosInstance.post(`/api/onedrive/scrape-and-train`,
                { fileIds: fileIdsToProcess },
                { params }
            );

            setError(response.data.message || 'Files processed successfully.'); 
            setShowScrapeTrainSuccessText(true); 

            fetchFiles(); 

            setSelectedRows([]); 
            setToggleCleared(!toggleCleared); 

        } catch (err) {
            const errorMessage = err.response?.data?.message || "Failed to process files. Please try again.";
            setError(errorMessage);
            setShowScrapeTrainSuccessText(false); 
        } finally {
            setIsProcessingScrapeTrain(false); 
        }
    };

    const handleRowSelected = ({ selectedRows: newSelectedRows }) => {
        // Limit selection if necessary, similar to ListTrainedData
        const MAX_SELECT_LIMIT_SCRAPE = 5; // Example limit
        if (newSelectedRows.length > MAX_SELECT_LIMIT_SCRAPE) {
            setError(`You can select up to ${MAX_SELECT_LIMIT_SCRAPE} files at once for scraping and training.`);
            setSelectedRows(newSelectedRows.slice(0, MAX_SELECT_LIMIT_SCRAPE));
            // Might need to inform DataTable to uncheck extras if it doesn't do it automatically
        } else {
            setSelectedRows(newSelectedRows);
            setError('');
        }
        setShowScrapeTrainSuccessText(false); 
    };
    
    const selectableRowDisabled = row => (row.is_scrape && row.is_trained && !row.is_manually_edited && !row.has_pending_update);

    // Modal Handlers
    const openEditModal = (doc) => { setCurrentDocumentForModal(doc); setIsEditModalOpen(true); };
    const closeEditModal = () => { setIsEditModalOpen(false); setCurrentDocumentForModal(null); };
    const handleUpdateFromEdit = () => { fetchFiles(); closeEditModal(); setError("Content updated successfully."); };

    const openSyncSettingsModal = (doc) => { setCurrentDocumentForModal(doc); setIsSyncSettingsModalOpen(true); };
    const closeSyncSettingsModal = () => { setIsSyncSettingsModalOpen(false); setCurrentDocumentForModal(null); };
    const handleSaveSyncSettings = async (settings) => {
        if (!currentDocumentForModal) return;
        setActionLoading('syncSave'); setError(null);
        try {
            
            const payload = { updates: [{ id: currentDocumentForModal.id, ...settings }] };
            await axiosInstance.post(`/api/onedrive/update-sync-settings`, payload, { params });
            fetchFiles();
            closeSyncSettingsModal();
            setError("Sync settings updated successfully.");
        } catch (err) {
            setError(err.response?.data?.message || "Failed to update sync settings.");
        } finally {
            setActionLoading(null);
        }
    };

    const openPendingUpdateModal = (doc) => { setCurrentDocumentForModal(doc); setIsPendingUpdateModalOpen(true); };
    const closePendingUpdateModal = () => { setIsPendingUpdateModalOpen(false); setCurrentDocumentForModal(null); };
    const handleResolvePendingUpdate = async (action, editedContent = null) => { // Add editedContent param
        if (!currentDocumentForModal) return;
        setActionLoading(action === 'accept' ? 'pendingAccept' : 'pendingReject'); setError(null);
        try {
            
            const payload = { action };
            if (action === 'accept' && editedContent !== null) { // Pass content if accepting
                payload.page_content = editedContent;
            }
            await axiosInstance.post(
                `/api/onedrive/resolve-pending-update/${currentDocumentForModal.id}`, 
                payload, { params }
            );
            fetchFiles();
            closePendingUpdateModal();
            setError(`Pending update ${action}ed successfully.`);
        } catch (err) {
            setError(err.response?.data?.message || `Failed to ${action} pending update.`);
        } finally {
            setActionLoading(null);
        }
    };

    const columns = [
        { name: "File Name", selector: (row) => row.filename, sortable: true, grow: 2, wrap: true, 
          cell: row => <Tooltip title={row.filename} arrow><span className="ellipsis-text">{row.filename}</span></Tooltip> 
        },
        { name: "Auto Sync", selector: row => row.is_auto_sync, sortable: true, width: '120px', center: true, cell: row => (
            <Tooltip title={row.is_auto_sync ? "Auto-sync ON. Click to manage." : "Auto-sync OFF. Click to manage."} arrow>
                <i className={`fa ${row.is_auto_sync ? 'fa-toggle-on text-success' : 'fa-toggle-off text-secondary'}`} 
                   style={{ fontSize: '1.6em', cursor: 'pointer' }} 
                   onClick={() => openSyncSettingsModal(row)} />
            </Tooltip>
        )},
        { name: "Schedule", sortable: true, width: '130px', center: true, 
          selector: row => row.sync_interval_days || row.default_interval_days, // for sorting
          cell: row => (
            <Tooltip title="Click to manage sync schedule" arrow>
                <span onClick={() => openSyncSettingsModal(row)} style={{ cursor: 'pointer', textDecoration: 'underline', color: '#0d6efd' }}>
                    {row.sync_interval_days ? `Every ${row.sync_interval_days} day${row.sync_interval_days > 1 ? 's' : ''}` : `Default (${row.default_interval_days}d)`}
                </span>
            </Tooltip>
        )},
        { name: "Last Check", selector: row => row.last_sync_check_date, sortable: true, width: '170px',
          format: row => row.last_sync_check_date ? new Date(row.last_sync_check_date).toLocaleString() : 'Never' 
        },
        { name: "Status", width: '150px', center: true, cell: row => (
            row.has_pending_update ? (
                <Tooltip title="System update pending review due to your manual edits." arrow>
                    <MuiButton size="small" variant="outlined" color="warning" onClick={() => openPendingUpdateModal(row)} startIcon={<i className="fa fa-exclamation-triangle" />} style={{padding: '2px 8px', fontSize: '0.8rem'}}>
                        Review
                    </MuiButton>
                </Tooltip>
            ) : (
                 <Tooltip title={row.is_trained ? (row.is_manually_edited ? "Manually edited, trained" : "Trained") : (row.is_scrape ? "Scraped, not trained" : "Not Scraped")} arrow>
                     <span style={{color: row.is_manually_edited && row.is_trained ? '#e67e22' : (row.is_trained ? 'green' : 'grey')}}>
                        {row.is_trained ? 'Trained' : (row.is_scrape ? 'Scraped' : 'Not Scraped')}
                        {row.is_manually_edited && row.is_trained ? '*' : ''}
                     </span>
                 </Tooltip>
            )
        )},
        {
            name: "Actions",
            width: '180px', // Increased width for new edit icon
            cell: (row) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', width: '100%' }}>
                    
                    {permission.canModify && (
                        <>
                        
                            <Tooltip title="Refresh from OneDrive & Retrain" arrow>
                                <button className="action-icon" onClick={() => handleRefreshFile(row.id)} disabled={loading || isProcessingScrapeTrain || refreshingRows[row.id]}>
                                    <i className={`fa ${refreshingRows[row.id] ? 'fa-spinner fa-spin' : 'fa-refresh'}`}></i>
                                </button>
                            </Tooltip>
                            <Tooltip title="Edit Content" arrow>
                                <button className="action-icon" onClick={() => openEditModal(row)} disabled={loading || isProcessingScrapeTrain || refreshingRows[row.id]}>
                                    <i className="fa fa-pencil"></i>
                                </button>
                            </Tooltip>
                            <Tooltip title="Open file in OneDrive" arrow>
                                <a href={row.webViewLink} target="_blank" rel="noopener noreferrer" className="action-icon" style={{pointerEvents: !row.webViewLink ? 'none' : 'auto'}}>
                                    <button className="action-icon-btn" disabled={!row.webViewLink || loading || isProcessingScrapeTrain} style={{ cursor: !row.webViewLink ? 'not-allowed' : 'pointer' }}>
                                        <i className="fa fa-external-link"></i>
                                    </button>
                                </a>
                            </Tooltip>
                        </> 
                    )}

                    {permission.canDelete && (

                        <Tooltip title="Remove from synced list & training" arrow>
                            <button className="action-icon delete" onClick={() => handleDeleteFile(row.id, row.filename)} disabled={loading || isProcessingScrapeTrain || refreshingRows[row.id]}>
                                <i className="fa fa-trash-o"></i>
                            </button>
                        </Tooltip>

                    )}
                </div>
            ),
            ignoreRowClick: true, allowOverflow: true, button: true,
        },
    ];

    const ExpandedComponent = ({ data }) => (
        <div style={{ padding: '15px', borderLeft: '3px solid #0d6efd', backgroundColor: '#f8f9fa', fontSize: '0.9em' }}>
            <p><strong>DB ID:</strong> {data.id || 'N/A'}</p>
            <p><strong>Filename:</strong> {data.filename || 'N/A'}</p>
            <div style={{ margin: '10px 0' }}>
                <strong>Summary:</strong>
                <div className="expanded-content-box">
                    {data.summary || 'N/A'}
                </div>
            </div>
            <div style={{ margin: '10px 0' }}>
                <strong>Page Content:</strong>
                <div className="expanded-content-box">
                    {data.page_content || 'N/A'}
                </div>
            </div>
            <p><strong>Scraped:</strong> {data.is_scrape ? 'Yes' : 'No'}</p>
            <p><strong>Trained:</strong> {data.is_trained ? 'Yes' : 'No'}</p>
            <p><strong>Manually Edited:</strong> {data.is_manually_edited ? 'Yes' : 'No'}</p>
            <p><strong>Pending Update:</strong> {data.has_pending_update ? 'Yes' : 'No'}</p>
            {data.has_pending_update && data.pending_update_content && (
                <div style={{ marginTop: '5px', maxHeight: '100px', overflowY: 'auto', border: '1px solid #ccc', padding: '5px' }}>
                    <strong>Pending Content Preview:</strong> {data.pending_update_content.substring(0,200)}...
                </div>
            )}
            <style jsx>{`
                .expanded-content-box { 
                    max-height: 150px; 
                    overflow-y: auto; 
                    border: 1px solid #e0e0e0; 
                    padding: 8px; 
                    background-color: #ffffff; 
                    margin-top: 5px; 
                    white-space: pre-wrap; /* Preserve whitespace and newlines */
                    word-break: break-word; /* Break long words */
                    font-size: 0.95em;
                    line-height: 1.4;
                }
            `}</style>
        </div>
    );

    const noRowsEffectivelySelected = selectedRows.filter(row => !(row.is_scrape && row.is_trained && !row.is_manually_edited)).length === 0;
    const scrapeAndTrainButtonDisabled = isProcessingScrapeTrain || selectedRows.length === 0 || noRowsEffectivelySelected;
    const processableCount = selectedRows.filter(row => !(row.is_scrape && row.is_trained && !row.is_manually_edited)).length;

    return (
        <div className="mt-3">
             <div className="row mb-3">
                <div className="col-md-6">
                    {selectedRows.length > 0 && permission.canModify && (
                        <div className="d-flex align-items-center">
                            <button
                                className="btn btn-primary"
                                onClick={handleScrapeAndTrain}
                                disabled={scrapeAndTrainButtonDisabled}
                            >
                                {isProcessingScrapeTrain ? (
                                    <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Processing...</>
                                ) : showScrapeTrainSuccessText ? (
                                    "Processed"
                                ) : (
                                    `Scrape & Train Selected (${processableCount})`
                                )}
                            </button>
                            {selectedRows.length > 0 && noRowsEffectivelySelected && !isProcessingScrapeTrain && !showScrapeTrainSuccessText && (
                                <small className="ms-2 text-muted">All selected are trained (no manual edits pending processing).</small>
                            )}
                        </div>
                    )}
                </div>
                <div className="col-md-6">
                    <input
                        type="text"
                        placeholder="Search synced files by name, type, summary..."
                        className="form-control"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </div>
            </div>

            {error && (
                <div className={`alert ${error.toLowerCase().includes("error") || error.toLowerCase().includes("failed") ? "alert-danger" : "alert-info"} my-2 alert-dismissible fade show`} role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} aria-label="Close"></button>
                </div>
            )}

            <DataTable
                columns={columns}
                data={filteredFiles}
                progressPending={loading && files.length === 0} 
                pagination
                responsive
                highlightOnHover
                pointerOnHover
                expandableRows
                expandableRowsComponent={ExpandedComponent}
                selectableRows
                onSelectedRowsChange={handleRowSelected}
                clearSelectedRows={toggleCleared}
                selectableRowDisabled={selectableRowDisabled}
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
            />

            {/* Edit Content Modal */}
            <MuiModal open={isEditModalOpen} onClose={closeEditModal} aria-labelledby="edit-onedrive-content-modal-title">
                <Box sx={muiModalStyle}>
                    {currentDocumentForModal && (
                        <EditOneDriveFileContent 
                            document={currentDocumentForModal} 
                            onClose={closeEditModal} 
                            onUpdate={handleUpdateFromEdit} 
                            isLoading={actionLoading === 'editSave'}
                            chatbotId={chatbotId}
                            permission={permission}
                        />
                    )}
                </Box>
            </MuiModal>

            {/* Sync Settings Modal */}
            {currentDocumentForModal && (
                <SyncSettingsModalForDrive
                    isOpen={isSyncSettingsModalOpen}
                    onClose={closeSyncSettingsModal}
                    onSave={handleSaveSyncSettings}
                    document={currentDocumentForModal}
                    isLoading={actionLoading === 'syncSave'}
                    permission={permission}
                />
            )}

            {/* Pending Update Modal */}
            {currentDocumentForModal && (
                 <PendingUpdateModalForDrive
                    isOpen={isPendingUpdateModalOpen}
                    onClose={closePendingUpdateModal}
                    onAccept={(editedHtml) => handleResolvePendingUpdate('accept', editedHtml)} // Pass edited HTML
                    onReject={() => handleResolvePendingUpdate('reject')} // Reject doesn't need content
                    originalContentHTML={currentDocumentForModal.page_content}
                    pendingContentHTML={currentDocumentForModal.pending_update_content}
                    documentUrl={currentDocumentForModal.filename} 
                    isLoadingAccept={actionLoading === 'pendingAccept'}
                    isLoadingReject={actionLoading === 'pendingReject'}
                    permission={permission}
                />
            )}
            <style jsx global>{`
                .ellipsis-text { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; display: block; max-width: 100%; }
                .action-icon { background: none; border: none; padding: 5px; cursor: pointer; color: #0d6efd; font-size: 1.1em; }
                .action-icon-btn { background: none; border: none; padding: 0; cursor: pointer; color: #0d6efd; font-size: 1em; vertical-align: middle; }
                .action-icon.delete { color: #dc3545; }
                .action-icon:hover, .action-icon-btn:hover { opacity: 0.7; }
                .action-icon:disabled, .action-icon-btn:disabled { opacity: 0.5; cursor: not-allowed; }
            `}</style>
        </div>
    );
}